import { fetchUnNotifiedReports } from '@dispatch/shared/services';
import {
  ReportsInfoRequest,
  ReportType,
  UnNotifiedReportsResponse,
} from '@dispatch/shared/types';
import { useAccessToken, useCurrentUser } from '@dispatch/web/auth';
import { useQuery } from '../useQuery';
import { useGetReportExportTimer } from './UseGetReportExportTimer';
import { useMarkReportsAsNotified } from './UseMarkReportsAsNotified';
import { useNotification } from '@dispatch/web/hooks';
import { useIsAuthenticated } from '@azure/msal-react';

export const useGetNotNotifiedReports = (
  enabled: boolean,
  setIsReportPollingEnabled: React.Dispatch<React.SetStateAction<boolean>>
) => {
  //create a hook to poll for unnotified reports
  const user = useCurrentUser();
  const userId = String(user?.userId ?? '');
  const { isAccessTokenSet } = useAccessToken();
  const isAuthenticated = useIsAuthenticated();

  const isReady = isAuthenticated && isAccessTokenSet;

  const { notifySuccess, notifyError } = useNotification();

  const { data: intervalTime } = useGetReportExportTimer(isReady);

  const { markReportsAsNotified } = useMarkReportsAsNotified();

  return useQuery<UnNotifiedReportsResponse[]>(
    ['poll-un-notified-reports', userId],
    async () => {
      const request: ReportsInfoRequest = {
        userId: String(userId),
        expand: false,
        filter: {
          reportTypes: [ReportType.Task],
          isNotified: false,
          pageSize: 20,
        },
      };
      return fetchUnNotifiedReports(request);
    },
    {
      enabled: enabled && !!intervalTime,
      refetchInterval: intervalTime,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          const completed = data
            .filter((r) => r.status.toLowerCase() === 'completed')
            .map((r) => r.id);
          const failed = data
            .filter((r) => r.status.toLowerCase() === 'failed')
            .map((r) => r.id);

          if (completed.length > 0) {
            notifySuccess(
              'Report Completed',
              'A report job completed successfully.'
            );
          }

          if (failed.length > 0) {
            notifyError('Report Failed', 'A report job has failed.');
          }

          const reportsToMark = [...completed, ...failed].map((id) => ({
            id,
            isNotified: true,
          }));

          if (reportsToMark.length > 0) {
            markReportsAsNotified({ reportsToMark });
          }
        } else {
          setIsReportPollingEnabled(false);
        }
      },
    }
  );
};
